import { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet-async';
import cn from 'classnames';
import { useMediaQuery } from 'usehooks-ts';
import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Drawer } from '@ebsco-ui/ebsco-ui';

import { Image, LinkElement } from '@app/components';
import { useBranding } from '@app/hooks';
import { BREAKPOINT, ROUTE } from '@app/constants';

import { AccountInfo } from './AccountInfo';
import { NavigationPanel } from './NavigationPanel';

import css from './Navigation.module.scss';

export const Navigation: FC = () => {
  const { logo } = useBranding();
  const { $t } = useIntl();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMobile = useMediaQuery(
    `(max-width: ${BREAKPOINT.SCREEN.TABLET_LG - 1}px)`
  );

  const burgerButtonId = 'burgerButton';

  const showDrawer = useCallback(
    (isOpen: boolean) => () => setIsDrawerOpen(isOpen),
    []
  );

  return (
    <div className={css.wrapper} data-auto="navigation-wrapper">
      <div className={cn(css.container, 'container')}>
        <LinkElement
          url={logo.link ?? ROUTE.search}
          data-auto="logo"
          className={css.logoLink}
        >
          <Helmet>
            <link rel="preload" as="image" href={logo.url} />
          </Helmet>
          <Image className="logo" src={logo.url} altText={logo.altText} />
          <span className={css.catalogLabel}>
            {$t({
              id: 'navigation.catalog',
              defaultMessage: 'Catalog',
            })}
          </span>
        </LinkElement>
        {isMobile ? (
          <div>
            <Button
              id={burgerButtonId}
              className={css.burgerButton}
              styleType="flat-icon"
              aria-label={$t({
                id: 'navigation.openNavigationMenu',
                defaultMessage: 'Open the navigation menu',
              })}
              onClick={showDrawer(true)}
            >
              <FontAwesomeIcon icon={faBars} />
            </Button>
            <Drawer
              drawerTag="section"
              className={css.drawer}
              position="start"
              titleElementId={burgerButtonId}
              isOpen={isDrawerOpen}
              onClose={showDrawer(false)}
            >
              <NavigationPanel isMobile setShowDrawer={setIsDrawerOpen} />
            </Drawer>
          </div>
        ) : (
          <NavigationPanel />
        )}
        <div className={css.accountInfo} data-auto="account-info">
          <AccountInfo />
        </div>
      </div>
    </div>
  );
};
